<script>
  import Layout from '../components/Layout.svelte';
  import CompanyMembers from '../components/CompanyMembers.svelte';
  import CompanyApiKeys from '../components/CompanyApiKeys.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import { companiesApi } from '../stores/api.js';
  import { loadCompanies } from '../stores/company.js';
  import { push } from 'svelte-spa-router';
  import { t } from '../i18n/index.js';
  import { Building2, ArrowLeft, Save, Archive } from '../lib/icons/index.js';

  /**
   * Edit form for a single company, reached from the pencil button on the
   * Aziende list (/companies/:id/settings).
   *
   * Mirrors the create form in Companies.svelte field by field, minus the
   * Partita IVA — the backend's PUT /api/companies/:companyId deliberately
   * leaves it out of its allowed-fields list because every invoice already
   * transmitted is tied to that number, so it is shown read-only rather than
   * offered as an input the server would silently ignore.
   *
   * Adds the one field that had no UI at all: conservazioneAdhesionDate, the
   * date the company signed the Agenzia delle Entrate conservazione agreement
   * (docs/conservazione-elettronica.md). It records a legal obligation, hence
   * the explanatory panel next to it.
   */

  export let params = {};

  // Same field styling as the Companies create form so the edit screen looks
  // like the screen the user filled in when the company was created.
  const inputCls = 'input';
  const inputStyle = '';
  const labelCls = 'block eyebrow mb-1';
  const labelStyle = 'color: var(--color-text-muted);';

  // Regime fiscale codes we ship a human label for. If the company carries a
  // code outside this set (e.g. created through the API) it is appended as-is
  // so the select never renders blank and never rewrites a value we cannot
  // describe.
  const REGIME_CODES = ['RF01', 'RF02', 'RF04', 'RF19'];

  let company = null;
  let loading = true;
  let saving = false;
  let error = null;
  let saved = false;
  let form = emptyForm();

  function emptyForm() {
    return {
      ragioneSociale: '',
      codiceFiscale: '',
      regimeFiscale: 'RF01',
      codiceDestinatario: '',
      pecDestinatario: '',
      sede: { indirizzo: '', cap: '', comune: '', provincia: '', nazione: 'IT' },
      conservazioneAdhesionDate: '',
      numberingPrefixFpr: 'FPR',
      numberingPrefixFpa: 'FPA',
      numberingPrefixAutofattura: 'AF01',
    };
  }

  /** ISO timestamp or date → the `YYYY-MM-DD` an <input type="date"> wants. */
  function toDateInput(value) {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  function formatDate(value) {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  $: companyId = params.id;
  // Load on mount and whenever the id in the URL changes. Deliberately not
  // paired with an onMount call: that would fire the same request twice.
  $: if (companyId) load(companyId);

  async function load(id) {
    loading = true;
    error = null;
    saved = false;
    try {
      company = await companiesApi.get(id);
      form = {
        ragioneSociale: company.ragioneSociale || '',
        codiceFiscale: company.codiceFiscale || '',
        regimeFiscale: company.regimeFiscale || 'RF01',
        codiceDestinatario: company.codiceDestinatario || '',
        pecDestinatario: company.pecDestinatario || '',
        sede: {
          indirizzo: company.sede?.indirizzo || '',
          cap: company.sede?.cap || '',
          comune: company.sede?.comune || '',
          provincia: company.sede?.provincia || '',
          nazione: company.sede?.nazione || 'IT',
        },
        conservazioneAdhesionDate: toDateInput(company.conservazioneAdhesionDate),
        numberingPrefixFpr: company.numberingPrefixFpr || 'FPR',
        numberingPrefixFpa: company.numberingPrefixFpa || 'FPA',
        numberingPrefixAutofattura: company.numberingPrefixAutofattura || 'AF01',
      };
    } catch (err) {
      company = null;
      error = err;
    } finally {
      loading = false;
    }
  }

  $: regimeOptions = Array.from(new Set([...REGIME_CODES, form.regimeFiscale].filter(Boolean)));

  async function handleSubmit(e) {
    e.preventDefault();
    saving = true;
    error = null;
    saved = false;
    try {
      // Only the fields the PUT endpoint accepts. Empty strings are sent as
      // null so clearing an optional field actually clears it server-side.
      company = await companiesApi.update(companyId, {
        ragioneSociale: form.ragioneSociale.trim(),
        codiceFiscale: form.codiceFiscale.trim() || null,
        regimeFiscale: form.regimeFiscale,
        codiceDestinatario: form.codiceDestinatario.trim() || null,
        pecDestinatario: form.pecDestinatario.trim() || null,
        sede: {
          indirizzo: form.sede.indirizzo.trim(),
          cap: form.sede.cap.trim(),
          comune: form.sede.comune.trim(),
          provincia: form.sede.provincia.trim(),
          nazione: form.sede.nazione.trim() || 'IT',
        },
        conservazioneAdhesionDate: form.conservazioneAdhesionDate || null,
        numberingPrefixFpr: form.numberingPrefixFpr.trim(),
        numberingPrefixFpa: form.numberingPrefixFpa.trim(),
        numberingPrefixAutofattura: form.numberingPrefixAutofattura.trim(),
      });
      form.conservazioneAdhesionDate = toDateInput(company.conservazioneAdhesionDate);
      // Refresh the store so the header switcher and the Aziende list show the
      // new ragione sociale without a reload.
      await loadCompanies();
      saved = true;
    } catch (err) {
      error = err;
    } finally {
      saving = false;
    }
  }
</script>

<Layout>
  <div class="max-w-4xl mx-auto">
    <div class="flex items-center gap-4 mb-2">
      <button type="button" class="btn btn-secondary btn-sm" on:click={() => push('/companies')}>
        <ArrowLeft size={16} class="mr-1" /> {$t('companySettings.backToList')}
      </button>
      <div class="flex items-center gap-3">
        <h1 class="section-title mb-0">
          {company?.ragioneSociale || $t('companySettings.title')}
        </h1>
      </div>
    </div>
    <p class="mb-6 text-sm max-w-3xl" style="color: var(--color-text-muted);">{$t('companySettings.subtitle')}</p>

    <ErrorBanner {error} />

    {#if loading}
      <div class="text-center py-8" role="status" aria-live="polite" style="color: var(--color-text-muted);">{$t('companySettings.loading')}</div>
    {:else if !company}
      <div class="card p-8 text-center" style="color: var(--color-text-muted);">{$t('companySettings.notFound')}</div>
    {:else}
      {#if saved}
        <div class="alert alert-success mb-4" role="status" aria-live="polite">
          <span class="alert-mark" aria-hidden="true">✓</span>
          <div>
            <p class="alert-title">{$t('companySettings.savedTitle')}</p>
            <p class="alert-message mt-1">{$t('companySettings.saved')}</p>
          </div>
        </div>
      {/if}

      <form on:submit={handleSubmit} on:input={() => (saved = false)} class="space-y-6">
        <!-- Identity ------------------------------------------------------ -->
        <div class="card p-6">
          <h2 class="eyebrow eyebrow-lg mb-1" style="color: var(--color-primary);">{$t('companySettings.sections.identity')}</h2>
          <p class="text-xs mb-4" style="color: var(--color-text-muted);">{$t('companySettings.sections.identityHint')}</p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="cs-ragione-sociale" class={labelCls} style={labelStyle}>{$t('companies.fields.ragioneSociale')}</label>
              <input id="cs-ragione-sociale" bind:value={form.ragioneSociale} required class={inputCls} style={inputStyle} />
            </div>
            <div>
              <label for="cs-partita-iva" class={labelCls} style={labelStyle}>
                {$t('companySettings.fields.partitaIva')}
                <InfoHint text={$t('companySettings.fields.partitaIvaLockedHint')} />
              </label>
              <!-- readonly rather than disabled: the value stays focusable and
                   copyable, and assistive tech announces it instead of
                   skipping it. -->
              <input id="cs-partita-iva" value={company.partitaIva} readonly aria-readonly="true"
                class="{inputCls} opacity-70 cursor-not-allowed" style={inputStyle} />
            </div>
            <div>
              <label for="cs-codice-fiscale" class={labelCls} style={labelStyle}>{$t('companies.fields.codiceFiscale')}</label>
              <input id="cs-codice-fiscale" bind:value={form.codiceFiscale} maxlength="16" class={inputCls} style={inputStyle} />
            </div>
            <div>
              <label for="cs-regime-fiscale" class={labelCls} style={labelStyle}>
                {$t('companies.fields.regimeFiscale')}
                <InfoHint text={$t('companies.fields.regimeFiscaleHint')} />
              </label>
              <select id="cs-regime-fiscale" bind:value={form.regimeFiscale} class={inputCls} style={inputStyle}>
                {#each regimeOptions as code}
                  <option value={code}>{code} - {$t(`companies.regimi.${code}`, undefined, code)}</option>
                {/each}
              </select>
            </div>
          </div>
        </div>

        <!-- Delivery address for inbound invoices ------------------------- -->
        <div class="card p-6">
          <h2 class="eyebrow eyebrow-lg mb-1" style="color: var(--color-primary);">{$t('companySettings.sections.delivery')}</h2>
          <p class="text-xs mb-4" style="color: var(--color-text-muted);">{$t('companySettings.sections.deliveryHint')}</p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="cs-codice-destinatario" class={labelCls} style={labelStyle}>
                {$t('companies.fields.codiceDestinatario')}
                <InfoHint term="codiceDestinatario" />
              </label>
              <input id="cs-codice-destinatario" bind:value={form.codiceDestinatario} maxlength="7" class={inputCls} style={inputStyle} />
            </div>
            <div>
              <label for="cs-pec" class={labelCls} style={labelStyle}>
                {$t('companies.fields.pec')}
                <InfoHint term="pec" />
              </label>
              <input id="cs-pec" type="email" bind:value={form.pecDestinatario} class={inputCls} style={inputStyle} />
            </div>
          </div>
        </div>

        <!-- Invoice numbering series --------------------------------------- -->
        <div class="card p-6">
          <h2 class="eyebrow eyebrow-lg mb-1" style="color: var(--color-primary);">{$t('companySettings.sections.numbering')}</h2>
          <p class="text-xs mb-4" style="color: var(--color-text-muted);">{$t('companySettings.sections.numberingHint')}</p>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label for="cs-prefix-fpr" class={labelCls} style={labelStyle}>{$t('companySettings.fields.numberingPrefixFpr')}</label>
              <input id="cs-prefix-fpr" bind:value={form.numberingPrefixFpr} maxlength="8" class={inputCls} style={inputStyle} />
            </div>
            <div>
              <label for="cs-prefix-fpa" class={labelCls} style={labelStyle}>{$t('companySettings.fields.numberingPrefixFpa')}</label>
              <input id="cs-prefix-fpa" bind:value={form.numberingPrefixFpa} maxlength="8" class={inputCls} style={inputStyle} />
            </div>
            <div>
              <label for="cs-prefix-af" class={labelCls} style={labelStyle}>{$t('companySettings.fields.numberingPrefixAutofattura')}</label>
              <input id="cs-prefix-af" bind:value={form.numberingPrefixAutofattura} maxlength="8" class={inputCls} style={inputStyle} />
            </div>
          </div>
          <p class="text-xs mt-3" style="color: var(--color-text-muted);">{$t('companySettings.fields.numberingPrefixHint')}</p>
        </div>

        <!-- Legal address ------------------------------------------------- -->
        <div class="card p-6">
          <h2 class="eyebrow eyebrow-lg mb-1" style="color: var(--color-primary);">{$t('companySettings.sections.address')}</h2>
          <p class="text-xs mb-4" style="color: var(--color-text-muted);">{$t('companySettings.sections.addressHint')}</p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <label for="cs-indirizzo" class={labelCls} style={labelStyle}>{$t('companies.fields.address')}</label>
              <input id="cs-indirizzo" bind:value={form.sede.indirizzo} required class={inputCls} style={inputStyle} />
            </div>
            <div>
              <label for="cs-cap" class={labelCls} style={labelStyle}>{$t('companies.fields.cap')}</label>
              <input id="cs-cap" bind:value={form.sede.cap} required maxlength="5" class={inputCls} style={inputStyle} />
            </div>
            <div>
              <label for="cs-comune" class={labelCls} style={labelStyle}>{$t('companies.fields.city')}</label>
              <input id="cs-comune" bind:value={form.sede.comune} required class={inputCls} style={inputStyle} />
            </div>
            <div>
              <label for="cs-provincia" class={labelCls} style={labelStyle}>{$t('companies.fields.province')}</label>
              <input id="cs-provincia" bind:value={form.sede.provincia} maxlength="2" class={inputCls} style={inputStyle} />
            </div>
            <div>
              <label for="cs-nazione" class={labelCls} style={labelStyle}>{$t('companies.fields.country')}</label>
              <input id="cs-nazione" bind:value={form.sede.nazione} maxlength="2" class={inputCls} style={inputStyle} />
            </div>
          </div>
        </div>

        <!-- Conservazione a norma ----------------------------------------- -->
        <div class="card p-6">
          <h2 class="eyebrow eyebrow-lg mb-1 flex items-center gap-2" style="color: var(--color-primary);">
            <Archive size={16} />
            {$t('companySettings.sections.conservazione')}
            <InfoHint term="conservazione" />
          </h2>
          <p class="text-xs mb-4 max-w-3xl" style="color: var(--color-text-muted);">{$t('companySettings.conservazione.intro')}</p>

          <div class="alert alert-info mb-4">
            <span class="alert-mark" aria-hidden="true">i</span>
            <div>
              <p class="alert-title">{$t('companySettings.conservazione.howTitle')}</p>
              <p class="alert-message mt-1">{$t('companySettings.conservazione.howBody')}</p>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label for="cs-conservazione" class={labelCls} style={labelStyle}>
                {$t('companySettings.fields.conservazioneDate')}
                <InfoHint text={$t('companySettings.fields.conservazioneDateHint')} />
              </label>
              <input id="cs-conservazione" type="date" bind:value={form.conservazioneAdhesionDate} class={inputCls} style={inputStyle} />
            </div>
            {#if company.conservazioneAdhesionDate}
              <div class="alert alert-success">
                <span class="alert-mark" aria-hidden="true">✓</span>
                <div>
                  <p class="alert-title">{$t('companySettings.conservazione.activeTitle')}</p>
                  <p class="alert-message mt-1">{$t('companySettings.conservazione.activeBody', { date: formatDate(company.conservazioneAdhesionDate) })}</p>
                </div>
              </div>
            {:else}
              <div class="alert alert-warning">
                <span class="alert-mark" aria-hidden="true">!</span>
                <div>
                  <p class="alert-title">{$t('companySettings.conservazione.missingTitle')}</p>
                  <p class="alert-message mt-1">{$t('companySettings.conservazione.missingBody')}</p>
                </div>
              </div>
            {/if}
          </div>
        </div>

        <p class="text-xs" style="color: var(--color-text-muted);">{$t('common.requiredLegend')}</p>

        <div class="flex gap-3">
          <button type="submit" disabled={saving}
            class="btn btn-primary btn-sm gap-2">
            <Save size={14} />
            {saving ? $t('companySettings.saving') : $t('companySettings.saveAction')}
          </button>
          <button type="button" on:click={() => push('/companies')}
            class="btn btn-secondary btn-sm">
            {$t('common.cancel')}
          </button>
        </div>
      </form>

      <!-- Pairing lives outside the form on purpose: it saves through its own
           endpoints, and a <form> inside another <form> is invalid HTML. -->
      <div class="mt-6">
        <CompanyMembers {companyId} />
      </div>

      <!-- Same reason: its own endpoints, so outside the form. The route is
           admin-only, which is also who the API lets manage keys. -->
      <div class="mt-6">
        <CompanyApiKeys {companyId} />
      </div>
    {/if}
  </div>
</Layout>
